import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { useRoles } from "../lib/useRoles";
import { toast } from "sonner";
import { Eye, ShieldAlert, RefreshCw } from "lucide-react";


// Master admin: preview the client portal as a role (read-only), carrying the current (possibly unsaved) matrix.
export function RolePreviewControl({ perms, dirty }) {
  const ROLE_LABEL = Object.fromEntries(useRoles().client.map((r) => [r.key, r.label]));
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data || []); if (r.data?.[0]) setCompanyId(r.data[0].company_id); }).catch(() => {});
  }, []);

  const preview = async () => {
    if (!companyId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/role-preview", { client_role: role, company_id: companyId, permissions: perms });
      localStorage.setItem("impersonation_token", data.token);
      window.location.href = "/client";
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  return (
    <div data-testid="role-preview-control" className="mt-5 border border-border bg-neutral-50 p-4 flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Preview as role</label>
        <select data-testid="role-preview-role" value={role} onChange={(e) => setRole(e.target.value)} className="border border-input bg-white px-3 py-2 text-sm">
          {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="min-w-[220px]">
        <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Company</label>
        <select data-testid="role-preview-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full border border-input bg-white px-3 py-2 text-sm">
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
        </select>
      </div>
      <button data-testid="role-preview-button" disabled={busy || !companyId} onClick={preview} className="inline-flex items-center gap-2 border border-black bg-white px-4 py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50">
        <Eye className="h-4 w-4" /> Preview portal
      </button>
      <p className="basis-full text-[11px] text-muted-foreground">
        Read-only preview using the matrix exactly as shown above{dirty ? " (including your unsaved changes)" : ""}. Nothing is saved by previewing.
      </p>
    </div>
  );
}

export function AccessDeniedLog() {
  const ROLE_LABEL = Object.fromEntries(useRoles().client.map((r) => [r.key, r.label]));
  const [rows, setRows] = useState(null);
  const load = () => api.get("/settings/client-access-denials?limit=50").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="access-denied-log" className="border border-border bg-white p-6 mt-6">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600" />
        <h2 className="font-bold tracking-tight">Blocked client attempts</h2>
        <button data-testid="access-denied-refresh" onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-xs border border-border px-2.5 py-1 hover:border-black transition-colors"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Client users who hit an area or action their role doesn't allow (last 90 days). Repeated entries suggest a role change is needed.</p>
      {rows === null ? <div className="text-sm text-muted-foreground mt-4">Loading…</div> : rows.length === 0 ? (
        <div data-testid="access-denied-empty" className="text-sm text-muted-foreground mt-4">No blocked attempts recorded.</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-2 pr-3">When</th><th className="py-2 pr-3">User</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Company</th><th className="py-2 pr-3">Area</th><th className="py-2 pr-3">Attempt</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.denial_id} data-testid={`denial-${r.denial_id}`} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                  <td className="py-2 pr-3">{ROLE_LABEL[r.client_role] || r.client_role}</td>
                  <td className="py-2 pr-3">{r.company_name || r.company_id}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.area}</td>
                  <td className="py-2 pr-3 text-xs"><span className="uppercase font-mono">{r.action}</span> · {r.method} <span className="text-muted-foreground">{(r.path || "").replace(/^.*\/api/, "")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
